import { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import { IntakeContext } from '../context/IntakeContext'
// import { Link } from "react-router-dom";

// Soft UI
import SoftBox from 'components/SoftBox'

// mui and icons
import {
  IconButton,
  Tooltip,
  Fade,
  Box,
  Grid,
  Typography,
  Card,
} from '@mui/material'
import HelpIcon from '@mui/icons-material/Help'
import BeachAccessIcon from '@mui/icons-material/BeachAccess'
import { useFieldFocus } from 'context/FieldFocusContext'

// custom components and additional imports
import {
  format,
  subYears,
  differenceInYears,
  addYears,
  addMonths,
  addDays,
  max,
  min,
} from 'date-fns'

import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

function getMinimumRetirementAge(birthYear) {
  if (birthYear < 1948) return { years: 55, months: 0 }
  if (birthYear === 1948) return { years: 55, months: 2 }
  if (birthYear === 1949) return { years: 55, months: 4 }
  if (birthYear === 1950) return { years: 55, months: 6 }
  if (birthYear === 1951) return { years: 55, months: 8 }
  if (birthYear === 1952) return { years: 55, months: 10 }
  if (birthYear >= 1953 && birthYear <= 1964) return { years: 56, months: 0 }
  if (birthYear === 1965) return { years: 56, months: 2 }
  if (birthYear === 1966) return { years: 56, months: 4 }
  if (birthYear === 1967) return { years: 56, months: 6 }
  if (birthYear === 1968) return { years: 56, months: 8 }
  if (birthYear === 1969) return { years: 56, months: 10 }
  return { years: 57, months: 0 }
}
function addRetirementAge(date, { years, months }) {
  let result = addYears(date, years)
  result = addMonths(result, months)
  return result
}

const ServiceCompDate = ({
  preHeader,
  header,
  subHeader,
  helpText,
  questionArray,
  alternateInput,
  handleNext,
}) => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const todaysDate = new Date()
  const birthYear = new Date(intakeState?.dateOfBirth).getFullYear()
  const minimumRetirementAgeYearsMonths = getMinimumRetirementAge(birthYear)
  const dateOfHireResolver = intakeState?.dateOfHire
    ? intakeState.dateOfHire
    : '1990-01-01'

  const eligibilityScenariosFirstFullyEligibleDate = min([
    max([
      addYears(new Date(dateOfHireResolver), 30),
      addRetirementAge(
        new Date(intakeState?.dateOfBirth),
        minimumRetirementAgeYearsMonths
      ),
    ]),
    max([
      addYears(new Date(dateOfHireResolver), 30),
      addYears(new Date(intakeState?.dateOfBirth), 57),
    ]),
    max([
      addYears(new Date(dateOfHireResolver), 20),
      addYears(new Date(intakeState?.dateOfBirth), 60),
    ]),
    max([
      addYears(new Date(dateOfHireResolver), 5),
      addYears(new Date(intakeState?.dateOfBirth), 62),
    ]),
  ])
  const eligibilityScenariosFirstFullyEligibleDateSpecialFERS = min([
    max([
      addYears(new Date(dateOfHireResolver), 20),
      addYears(new Date(intakeState.dateOfBirth), 50),
    ]),
    addYears(new Date(dateOfHireResolver), 25),
  ])
  const eligibilityScenariosFirstFullyEligibleDateFSPS = min([
    max([
      addYears(new Date(dateOfHireResolver), 20),
      addYears(new Date(intakeState.dateOfBirth), 50),
    ]),
    max([
      addYears(new Date(dateOfHireResolver), 5),
      addYears(new Date(intakeState.dateOfBirth), 62),
    ]),
  ])
  const eligibilityScenariosFirstFullyEligibleDateCSRS = min([
    addYears(new Date(intakeState.dateOfBirth), 55),
  ])
  // const eligibilityScenariosFirstFullyEligibleDateCSRSFrozen = min([
  //   max([
  //     addYears(new Date(dateOfHireResolver), 30),
  //     addYears(new Date(intakeState.dateOfBirth), minimumRetirementAge),
  //   ]),
  // ])

  // check for all retiement systems with fers being the fallback
  const firstFullyEligibleDate =
    intakeState?.retirementSystem === 'special_fers_(leo,_atc_etc.)'
      ? eligibilityScenariosFirstFullyEligibleDateSpecialFERS
      : intakeState?.retirementSystem ===
        'foreign_service_officer_pension_(fsps)'
      ? eligibilityScenariosFirstFullyEligibleDateFSPS
      : intakeState?.retirementSystem === 'csrs'
      ? eligibilityScenariosFirstFullyEligibleDateCSRS
      : eligibilityScenariosFirstFullyEligibleDate
  const soonestFullyEligibleDate = new Date(
    firstFullyEligibleDate.getFullYear(),
    firstFullyEligibleDate.getMonth() + 1,
    0
  )
  const soonestFullyEligibleFormattedDate =
    soonestFullyEligibleDate < new Date()
      ? "You're Eligible to Retire Now!"
      : format(soonestFullyEligibleDate, 'MMMM do, yyyy')
  const soonestFullyEligibleFormattedDateToStore = format(
    soonestFullyEligibleDate < new Date()
      ? new Date(todaysDate.getFullYear(), todaysDate.getMonth() + 1, 0)
      : soonestFullyEligibleDate,
    'yyyy-MM-dd'
  )

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992)
  const fieldId = 'compDateHelp'
  const newHelpText = `Your official date of hire with the government, including any bought back military time. If you don't know, put your best guess or check your latest SF-50.`
  const fieldId2 = 'fraHelp'
  const newHelpText2 = `The date you can recieve an Unreduced Pension, Full Access to your TSP, and Lifetime access to Federal Healthcare.`

  const { handleFocus, handleBlur, registerHelpField } = useFieldFocus()
  useEffect(() => {
    registerHelpField(fieldId, newHelpText)
    registerHelpField(fieldId2, newHelpText2)
  }, [fieldId])

  const fieldMap = (data) => {
    return (
      <Grid container>
        {data.map((question) => (
          <Grid
            item
            key={question.id}
            xs={12}
            mb={2}
            sx={{ display: question.hiddenField ? 'none' : 'block' }}
          >
            {renderMuiFormField({
              handleNext,
              autoFocus: false,
              id: question.id,
              label: (
                <>
                  <span>
                    {question.label}
                    {question.required && (
                      <span style={{ color: 'red' }}> *</span>
                    )}
                    {question.helpText && isMobile && (
                      <Tooltip
                        TransitionComponent={Fade}
                        title={question.helpText}
                        placement="top"
                        color="info"
                        enterTouchDelay={0}
                        leaveTouchDelay={3000}
                      >
                        <IconButton size="small" color="dark">
                          <HelpIcon sx={{ marginTop: '-6px' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </span>
                </>
              ),
              placeholder: question.placeholder,
              inputType: question.inputType,
              options: question.options,
              value: intakeState?.[question.id],
              defaultDate: question?.defaultDate || '1980-01-01',
              onChange: (value) =>
                updateIntakeStateAtKey({
                  [question.id]: value,
                  dateOfPlannedRetirement:
                    soonestFullyEligibleFormattedDateToStore,
                }),
            })}
          </Grid>
        ))}
      </Grid>
    )
  }

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box
        onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
        onMouseLeave={() => handleBlur()}
      >
        <Box>
          <Typography variant="subtitle1" fontSize={{ xs: '16px', sm: '20px' }}>
            {preHeader || ''}
          </Typography>
        </Box>
        <Box mb={1}>
          <Typography variant="h4" fontSize={{ xs: '20px', sm: '24px' }}>
            {header || "Let's start with your basic information:"}
            {helpText !== '' ? (
              <Tooltip
                TransitionComponent={Fade}
                title={
                  helpText || 'This helps us calculate your retirement benefits'
                }
                placement="top"
                color="info"
                enterTouchDelay={0}
                leaveTouchDelay={3000}
              >
                <IconButton size="small" color="dark">
                  <HelpIcon sx={{ marginTop: '-10px' }} />
                </IconButton>
              </Tooltip>
            ) : (
              <></>
            )}
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{ lineHeight: '1.2' }}
          fontSize={{ xs: '16px', sm: '20px' }}
        >
          {subHeader ||
            'Let us know your name and other basic details about you!'}
        </Typography>
        <Box pt={3} pb={1}>
          {fieldMap(questionArray)}
          {alternateInput && <div>Alt Input Exists</div>}
        </Box>
      </Box>
      <Box
        mb={3}
        p={3}
        borderRadius={2}
        sx={{ backgroundColor: '#F0F4F8' }}
        display={dateOfHireResolver === '1990-01-01' ? 'none' : 'block'}
        onMouseEnter={(e) => handleFocus(fieldId2, e.currentTarget)}
        onMouseLeave={() => handleBlur()}
      >
        <Card
          sx={{
            textAlign: 'center',
            boxShadow: 1,
            borderRadius: 2,
            padding: { xs: '1rem', md: '2rem' },
          }}
        >
          <Typography variant="h4" fontSize={{ xs: '16px', sm: '20px' }}>
            Your First Fully Eligible Date
            {isMobile && (
              <Tooltip
                TransitionComponent={Fade}
                title={
                  'The date you can recieve an Unreduced Pension, Full Access to your TSP, and Lifetime access to Federal Healthcare'
                }
                placement="top"
                color="info"
                enterTouchDelay={0}
                leaveTouchDelay={3000}
              >
                <IconButton size="small" color="dark">
                  <HelpIcon sx={{ marginTop: '-10px' }} />
                </IconButton>
              </Tooltip>
            )}
          </Typography>
          <Typography
            variant="h3"
            fontWeight="bold"
            fontSize={{ xs: '18px', md: '30px' }}
            mt={1}
            color="#344767"
          >
            {soonestFullyEligibleFormattedDate}
            <BeachAccessIcon
              sx={{
                marginLeft: '10px',
                marginBottom: '-3px',
              }}
            />
          </Typography>
        </Card>
        {/* <Typography variant="body2" mt={2} sx={{ lineHeight: '1.3' }}>
          Did you find this helpful? Create an account to see loads of other
          useful information to help you plan for your retirment.
        </Typography> */}
      </Box>
    </Box>
  )
}

export default ServiceCompDate
